import { ref, onBeforeUnmount, UnwrapRef, watch, reactive } from '@vue/composition-api';
// composableはサービスに依存できないからタイプ以外は読み込んでいない。
// TODO: このタイプや、分岐に使用しているリテラル値は、リファクタして外出しにする必要があると思う。
// GlobalAPIErrorContextから設計して、コピーして作ったからこうなってる。
// コンテキストより低いレイヤーのviewsで使うから絶対に循環参照にはならないんだけどちょっと気持ち悪い。
import { camelCase, cloneDeep, deepEqual, getUniqueArray } from '@berlin-front/common/utils';
import { broadcast, API_ERROR, API_ERROR_TYPE } from '@/broadcast';

type UseErrorOptions = {
  /**
   * エラーレスポンスのparamを別名にマッピングするための関数です。
   * フォームのstateのキーに対してマッピングするのが通常のユースケースです。
   */
  fieldMapper: Record<string, string> | ((fieldName: string) => string);

  /**
   * fieldMapperで定義されていないparamは全てdefaultFieldNameに変換されます。
   * 指定しない場合はparam名は変換前後で変わりません。
   */
  defaultFieldName?: string;

  /**
   * ここで与えられたstateの状態を監視し、値が変更になったキーのエラーをクリアします。
   * 指定しない場合、自力でクリアを実行する必要があります。
   */
  state?: UnwrapRef<Record<string, unknown>>;

  /**
   * 相関フィールド情報。
   *
   * エラーフィールドの相関関係を記述します。
   * 一つのエラーをクリアする際には、相関関係のあるフィールドのエラーもクリアされます。
   *
   * 相関関係は、複数のパラメータ文字列からなる配列です。
   * このフィールドにセットするのは、相関関係の配列です。相関関係自体が配列なので、文字列配列の配列になります。
   *
   * @example
   * // 例えば、paramA paramBの間に相関関係があり、paramC paramD paramEの間にも相関関係があるばあい
   * [
   *   ['paramA', 'paramB'],
   *   ['paramC', 'paramD', 'paramE'],
   * ]
   */
  correlations?: string[][];
};

export default (options?: UseErrorOptions) => {
  const opt = {
    fieldMapper: (val: string) => val,
    state: reactive({} as Record<string, unknown>),
    correlations: [] as string[][],
    ...options,
  };

  const fieldMapper =
    typeof opt.fieldMapper === 'object'
      ? (value: string): string =>
          (opt.fieldMapper as Record<string, string>)?.[value] ?? opt?.defaultFieldName ?? value
      : opt.fieldMapper;
  const { state } = opt;

  const { correlations } = opt;

  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  /**
   * エラーメッセージのクリア要求に対してディレイをかけるための関数。
   *
   * APIで発生するエラーはBerlinBffからMessagePort#postMessageで通知される。\
   * これは非同期処理で、ややディレイがかかる。\
   * 即、エラーメッセージのクリアを行おうとすると、クリア要求がエラー通知を追い越してしまうケースがある。\
   * そのため、clear要求にも同じレベルのディレイをかけたい。\
   * @param task
   */
  const onMicroDelay = (task: () => void) => {
    const messageChannel = new MessageChannel();
    messageChannel.port1.start();
    messageChannel.port2.start();
    messageChannel.port2.addEventListener('message', () => {
      messageChannel.port1.close();
      messageChannel.port2.close();
      // アンマウントされてる場合は何もしない。
      if (unmounted) return;
      task();
    });
    messageChannel.port1.postMessage('');
  };

  /**
   * APIで発生したエラーをパラメタ別に保持します。
   * param指定のないエラーについては、空文字列をキーとして格納されます（defaultFieldNameを使用していない場合）。
   */
  const errors = ref<Record<string, string[]>>({});
  /**
   * 全てのエラーメッセージをクリアします。
   * 再びAPIを呼ぶ前に一度、クリアする必要があります。
   */
  const clearAll = () => {
    onMicroDelay(() => {
      errors.value = {};
    });
  };
  /**
   * パラメタ名を指定してエラーメッセージをクリアします。
   * @param params
   */
  const clear = (...params: string[]) => {
    onMicroDelay(() => {
      // エラーオブジェクトのキーはネストしてないことが前提なんで、浅いコピーを取る。
      // （もしネストするのであればどっと区切りでフラットなRecordの表現にしたらいいと思う。）
      const copy = { ...errors.value };
      getUniqueArray(
        params.flatMap((param) => [...correlations.filter((correlation) => correlation.includes(param)).flat(), param]),
      ).forEach((param) => {
        delete copy[param];
      });
      errors.value = copy;
    });
  };

  // クリアのロジック、cloneDeepやdeepEqual使ってるがネストしたキーを扱う仕組みではないことに注意。
  let prevState = cloneDeep(state);
  watch(state, () => {
    const clearTargetKeys = Object.keys(state).filter((key) => !deepEqual(state[key], prevState[key]));
    clear(...clearTargetKeys);
    prevState = cloneDeep(state);
  });

  const unlisten = broadcast.listen(API_ERROR, (payload) => {
    if (payload.errorType === API_ERROR_TYPE.BUSINESS_ERROR) {
      if (typeof payload.errorResponse?.details !== 'string') {
        payload.errorResponse?.details.errors.forEach((error) => {
          const params = error.params.length > 0 ? error.params : [''];
          const value = { ...errors.value };
          params
            .map((paramName) => fieldMapper(camelCase(paramName)))
            .forEach((paramName) => {
              value[paramName] = getUniqueArray([...(value[paramName] ?? []), ...error.messages]);
            });
          errors.value = value;
        });
      }
    }
  });
  onBeforeUnmount(unlisten);

  return {
    errors,
    clear,
    clearAll,
  };
};
